import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';

import { FEATURES } from 'appConstants';
import { makeSelectCustomer } from 'containers/Customer/selectors';
import { C360_ROUTE_SEGMENT } from 'containers/Intelligence/customer360/constants';
import {
  ICustomerIdentity,
  customerDisplayName
} from 'containers/Intelligence/customer360/customerIdentity';
import { useFeatureFlags } from 'hooks';

import {
  ORBIT_SCREEN_CUSTOMER_360,
  ORBIT_SCREEN_CUSTOMER_INSIGHTS,
  ORBIT_SCREEN_CUSTOMER_PROFILE
} from './constants';
import { OrbitEntryContext } from './types';

// One frozen instance for "this route carries no context": a fresh `{}` per navigation would churn
// the memoised context value on every route change that adds nothing.
const EMPTY_ENTRY_CONTEXT: OrbitEntryContext = Object.freeze({});

const CUSTOMER_INSIGHTS_PATH = '/p/intelligence/customer-insights';
const CUSTOMER_360_PATH = `/p/customers/show/:id/${C360_ROUTE_SEGMENT}`;
/**
 * `Customer Profile`, the host of Desktop-7. `containers/Customer/index.tsx` routes it to the
 * customer edit page, and its identity band is what the frame draws: name, Perx ID, alternate id.
 *
 * `Financial Health Insights`, the host of Desktop-8 and Desktop-12, gets NO entry of its own. It is
 * not a route in this dashboard: it is a section of Customer 360 (`FinancialHealthSection`, testid
 * `c360-financial-health-insights`), so it already resolves to `customer_360`. TODO(V11): if the
 * designer means a screen the dashboard does not have, that is a product question, not a path to
 * invent here.
 */
const CUSTOMER_PROFILE_PATH = '/p/customers/show/:id';

export const resolveOrbitEntryContext = (pathname: string): OrbitEntryContext => {
  if (matchPath(CUSTOMER_INSIGHTS_PATH, pathname) !== null) {
    return { screen: ORBIT_SCREEN_CUSTOMER_INSIGHTS };
  }

  const customerId = matchPath(CUSTOMER_360_PATH, pathname)?.params.id;

  if (customerId) {
    return { screen: ORBIT_SCREEN_CUSTOMER_360, customer_id: customerId };
  }

  const profileId = matchPath(CUSTOMER_PROFILE_PATH, pathname)?.params.id;

  if (profileId) {
    return { screen: ORBIT_SCREEN_CUSTOMER_PROFILE, customer_id: profileId };
  }

  return EMPTY_ENTRY_CONTEXT;
};

/**
 * Drops the display-only customer name. Everything that leaves the browser or outlives the tab goes
 * through this: the contract (PLA-8839 §2) has no key for a name, and a request body and a
 * localStorage record are both worse places for one than the screen it is already on.
 */
export const toOrbitWireContext = (context: OrbitEntryContext): OrbitEntryContext => {
  if (context.customer_name === undefined) {
    return context;
  }

  const { customer_name: _customerName, ...wire } = context;

  return wire;
};

export const useOrbitEntryContext = (): OrbitEntryContext => {
  const { pathname } = useLocation();
  const suiteEnabled = useFeatureFlags()[FEATURES.INTELLIGENCE_SUITE.key] === true;
  const selectCustomer = useMemo(makeSelectCustomer, []);
  const customer = useSelector(selectCustomer) as ICustomerIdentity | null | undefined;

  // Both derived to strings before they reach the memo: depending on the customer object itself
  // would hand the provider a new context on every unrelated write to that slice.
  const loadedName = customerDisplayName(customer);
  const loadedId = customer?.id === undefined || customer?.id === null ? '' : String(customer.id);

  return useMemo(() => {
    // Each context below frames an Intelligence Suite question, and without the module the two
    // Intelligence routes render Forbidden, so the panel opens on its home state instead. PLA-9754.
    if (!suiteEnabled) {
      return EMPTY_ENTRY_CONTEXT;
    }

    const context = resolveOrbitEntryContext(pathname);

    // The slice is whatever customer was last fetched, and only Customer 360 fetches one. Matching
    // it against the route's own id is what stops a previous customer's name being attached to this
    // one, and what leaves the name off a profile page that never dispatched a fetch.
    if (
      loadedName === '' ||
      context.customer_id === undefined ||
      loadedId !== context.customer_id
    ) {
      return context;
    }

    return { ...context, customer_name: loadedName };
  }, [pathname, loadedName, loadedId, suiteEnabled]);
};

export default useOrbitEntryContext;
